import { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface PasswordStrengthIndicatorProps {
  password: string;
}

type StrengthLevel = 'weak' | 'medium' | 'strong';

const getPasswordStrength = (password: string): { level: StrengthLevel; score: number } => {
  if (!password) return { level: 'weak', score: 0 };
  
  let score = 0;
  
  // Length checks
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  
  // Character variety checks
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  
  // Determine level
  if (score <= 2) return { level: 'weak', score };
  if (score <= 4) return { level: 'medium', score };
  return { level: 'strong', score };
};

const strengthConfig = {
  weak: {
    label: 'Weak',
    color: 'bg-destructive',
    textColor: 'text-destructive',
    bars: 1,
  },
  medium: {
    label: 'Medium',
    color: 'bg-amber-500',
    textColor: 'text-amber-500',
    bars: 2,
  },
  strong: {
    label: 'Strong',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-500',
    bars: 3,
  },
};

export const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  const strength = useMemo(() => getPasswordStrength(password), [password]);
  const config = strengthConfig[strength.level];
  
  if (!password) return null;
  
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex gap-1">
        {[1, 2, 3].map((bar) => (
          <div
            key={bar}
            className={cn(
              'h-1 flex-1 rounded-full transition-all duration-300',
              bar <= config.bars ? config.color : 'bg-muted'
            )}
          />
        ))}
      </div>
      <p className={cn('text-xs transition-colors', config.textColor)}>
        {config.label}
      </p>
    </div>
  );
};
